import { IFrame, IMultiModelFrameMap } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import {
    IConnectorModel,
    IPointerInfo,
    IPointerOffsetInfo,
} from '@src/slide/domain/ui-model/element-modeles/Interface/connector-model.interface';
import * as Lines from '@src/slide/infrastructure/module/dependent/line-info';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { ISlideBaseAppServices } from '@src/slide/application/entry/toc/base/slide-base-app-services.interface';
import { ShapeType } from '@src/slide/infrastructure/module/dependent/shape-info/graphic-type';
import expect from '@src/slide/infrastructure/module/independent/assert/expect';
import { IElementBaseModel } from '../../Interface/base/element-base-model.interface';
import { IPoint } from '@src/slide/infrastructure/module/independent/graphic/point/point.interface';
import { UIModelType } from '../../constant';
import { enumerateGroup } from '@src/slide/domain/ui-model/element-modeles/property/group-operation/enumerate';

export interface UpdateSytle {
    operationFrame: IFrame|null;
    controlValues?: number[]|undefined;
    bgLine?: string|null;
}

export class ConnectorCaculater {
    private model: IConnectorModel;
    public constructor(connectorModel) {
        this.model = connectorModel;
    }

    public getUpdateStyle(offsetFrame: Partial<IFrame>|null, startEndOffset?: IPointerOffsetInfo): UpdateSytle {
        if ((offsetFrame || startEndOffset)) {
            const frame = this.model.getFrame?.().getFrameInfo(false); // 连接线的当前frame
            const { startP, endP } = this.model.getStartEndPoints(frame, startEndOffset);
            if (this.onlyMove()) {
                // 如果只有一端连接了元素，并且元素正在移动，则连接线也跟随移动
                const isConnectorInGroup = this.isConnectorInGroup();
                const offsetLeft = isConnectorInGroup ? 0 : offsetFrame?.left;
                const offsetTop = isConnectorInGroup ? 0 : offsetFrame?.top;
                const obj = this.model.getUpdateStylePropsForOnlyMove({ left: offsetLeft ?? 0,
                    top: offsetTop ?? 0 });
                return { operationFrame: obj };
            }
            return this.getOperationFrameForNotOnlyMove(startP, endP);
        }
        return { operationFrame: null };
    }

    // 此处获取的是端点相对于父级元素的相对坐标，入参是相对于slide的绝对坐标
    public updatePointInGroup(point: IPoint): IPoint {
        const isGroupChildren = !!(this.model.parent && this.model.parent.type === UIModelType.GROUP);

        if (isGroupChildren) {
            const newFrame = this.model.getFrame().convertFrameOnSlideToSelf({
                left: point.x,
                top: point.y,
                width: 0,
                height: 0,
            });
            point.x = newFrame.left;
            point.y = newFrame.top;
        }
        return point;
    }

    /**
     * 是否为肘形/曲线连接线
     * @param lineType
     * @private
     */
    private validateLineType(lineType: ShapeType): boolean {
        return ((expect(lineType).isString() && lineType.startsWith('bentConnector'))
                || (expect(lineType).isString() && lineType.startsWith('curvedConnector')));
    }

    /**
     * 获取连接线需要更新的style信息
     */
    private getStyleProps = (
        startP: IPointerInfo, endP: IPointerInfo, lineType: ShapeType,
        toUpdateModelFrameMap?: IMultiModelFrameMap,
    ): UpdateSytle => {
        let { controlValues, bgLine } = this.model.props.style;
        const frameMap = toUpdateModelFrameMap;
        if (this.validateLineType(lineType)) {
            if (this.connectedNum() !== 0) {
                const style = this.model.getConnectedValidateLineTypeStyle(startP, endP, lineType, frameMap);
                return {
                    ...style,
                    operationFrame: style.frame,
                };
            }
            bgLine = lineType.startsWith('bentConnector') ? Lines.LineType.BENTCONNECTOR3 : Lines.LineType.CURVEDCONNECTOR3;
            controlValues = [0.5];
        }
        const operationFrame = this.getOffsetFrame(this.updatePointInGroup(startP), this.updatePointInGroup(endP));
        return { operationFrame, controlValues, bgLine };
    };

    /**
     * 获取不为肘形/曲线连接线 或者 两端未连接元素的待更新的frame
     */
    private getOffsetFrame = (startP: IPointerInfo, endP: IPointerInfo): IFrame => {
        let flipH = 0;
        let flipV = 0;
        let startX = startP.x;
        let startY = startP.y;
        let endX = endP.x;
        let endY = endP.y;
        if (startX > endX) {
            const t = startX;
            startX = endX;
            endX = t;
            flipH = 1;
        }
        if (startY > endY) {
            const t = startY;
            startY = endY;
            endY = t;
            flipV = 1;
        }
        return {
            left: startX,
            top: startY,
            width: endX - startX,
            height: endY - startY,
            flipH,
            flipV,
            rotate: 0,
        };
    };

    /**
     * 更新连接线的操作位置信息（非单纯的整体移动）
     * @param startPoint 端点
     * @param endPoint 端点
     * @private
     */
    private getOperationFrameForNotOnlyMove(startPoint: IPointerInfo, endPoint: IPointerInfo): UpdateSytle {
        const startP = startPoint;
        const endP = endPoint;
        const lineType = this.model.props.style.bgLine || Lines.LineType.STRAIGNTCONNECTOR1;
        const { operationFrame, bgLine, controlValues } = this.getStyleProps(startP, endP, lineType);
        this.model.rotateInGroup = operationFrame?.rotate;
        return { controlValues, bgLine, operationFrame };
    }

    /**
     * 获取连接线关联的图形数量
     * @param connectorModel uiModel
     * @private 数量
     */
    private connectedNum(): number {
        let num = 0;
        if (this.model.startUIModel) {
            num += 1;
        }
        if (this.model.endUIModel) {
            num += 1;
        }
        return num;
    }

    /**
     * 判断连接线是否与所连接的图形在同一个组合内
     */
    private isConnectorInGroup(): boolean {
        const startModelRootGroup = this.model.startUIModel?.rootGroupModel;
        const endModelRootGroup = this.model.endUIModel?.rootGroupModel;
        const connectorRootGroup = this.model.rootGroupModel;
        if (this.connectedNum() === 1) {
            if (this.isRootGroupModelSelected(this.model.startUIModel)
            && connectorRootGroup?.key === startModelRootGroup?.key) {
                return true;
            }
            if (this.isRootGroupModelSelected(this.model.endUIModel)
                && connectorRootGroup?.key === endModelRootGroup?.key) {
                return true;
            }
            return false;
        }
        if (this.isRootGroupModelSelected(this.model.startUIModel)
                && this.isRootGroupModelSelected(this.model.endUIModel)
                && (connectorRootGroup?.key === startModelRootGroup?.key
                    || connectorRootGroup?.key === endModelRootGroup?.key)) {
            return true;
        }
        return false;
    }

    private isRootGroupModelSelected(model: IElementBaseModel|null) {
        const rootGroupModel = model?.rootGroupModel;
        return rootGroupModel?.selected && !model?.selected;
    }

    private onlyMove(): boolean {
        if (this.getEditingStateManager().isDragging) {
            if (this.connectedNum() === 1
            || (this.model.startUIModel?.selected && this.model.endUIModel?.selected)
            || this.isOnlySelectRootGroupModel()) {
                return true;
            }
        }
        if (this.getEditingStateManager().isResizing) {
            if (this.isOnlySelectRootGroupModel()) {
                return true;
            }
        }
        return false;
    }

    private isOnlySelectRootGroupModel() {
        const startModelRootGroup = this.model.startUIModel?.rootGroupModel;
        const endModelRootGroup = this.model.endUIModel?.rootGroupModel;
        if (startModelRootGroup?.selected && !this.model.startUIModel?.selected
            && endModelRootGroup?.selected && !this.model.endUIModel?.selected) {
            return true;
        }
        return false;
    }


    private getEditingStateManager() {
        const appServices = AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices;
        return appServices.viewableServiceLayer.editingStateManager;
    }
}
